import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { KeyRound, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const token = params.get('token') || '';
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { setError(t('reset_mismatch')); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate('/connexion'), 2500);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : t('reset_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-[color:var(--color-surface)] border border-white/10 p-8">
        <div className="text-center mb-8">
          <Link to="/">
            <span className="text-2xl font-light tracking-wider text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              O'<span className="text-[color:var(--color-primary)]">Delicieuse</span> <span className="text-sm uppercase tracking-widest">Wholesale</span>
            </span>
          </Link>
        </div>
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-[color:var(--color-primary)] mx-auto mb-4" />
            <h1 className="text-2xl text-white font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('reset_done_title')}</h1>
            <p className="text-zinc-400 text-sm">{t('reset_done_desc')}</p>
          </div>
        ) : !token ? (
          <p className="text-red-400 text-sm text-center" data-testid="reset-no-token">{t('reset_invalid')}</p>
        ) : (
          <>
            <h1 className="text-3xl text-white font-light text-center mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('reset_title')}</h1>
            <p className="text-zinc-400 text-sm text-center mb-8">{t('reset_desc')}</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="input-label">{t('reset_new_password')}</label>
                <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={6}
                  className="w-full input-field" data-testid="reset-password-input" />
              </div>
              <div>
                <label className="input-label">{t('reset_confirm_password')}</label>
                <input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} required minLength={6}
                  className="w-full input-field" data-testid="reset-password-confirm-input" />
              </div>
              {error && <p className="text-red-500 text-sm" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-[color:var(--color-primary)] text-black px-6 py-3 hover:bg-[color:var(--color-primary-hover)] transition-colors font-medium disabled:opacity-50"
                data-testid="reset-submit-btn">
                <KeyRound className="w-5 h-5" /> {loading ? '...' : t('reset_submit')}
              </button>
            </form>
          </>
        )}
        <div className="mt-6 text-center">
          <Link to="/connexion" className="text-zinc-500 hover:text-[color:var(--color-primary)] text-sm" data-testid="reset-back-login">{t('forgot_back_login')}</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
